"use client";

import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { Container } from "@/components/ui/Container";
import { HeaderNav } from "@/components/HeaderNav";
import { Footer } from "@/components/Footer";
import { ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { LanguageProvider } from "@/contexts/LanguageContext";

const translations = {
  en: {
    backToHome: "Back to home",
    title: "Privacy",
    titleHighlight: "Policy",
    lastUpdated: "Last updated: January 2026",
    sections: [
      {
        title: "1. Information We Collect",
        content: "We collect information you provide directly to us, such as your name, email address, phone number, and any other information you choose to provide when contacting us or using our services."
      },
      {
        title: "2. How We Use Your Information",
        content: "We use the information we collect to provide, maintain, and improve our services, communicate with you about projects and updates, send you technical notices and support messages, and respond to your comments and questions."
      },
      {
        title: "3. Information Sharing",
        content: "We do not share, sell, or rent your personal information to third parties for their marketing purposes. We may share information with service providers who assist us in operating our website and conducting our business."
      },
      {
        title: "4. Data Security",
        content: "We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the Internet is 100% secure."
      },
      {
        title: "5. Cookies and Tracking",
        content: "We may use cookies and similar tracking technologies to track activity on our website and hold certain information. You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent."
      },
      {
        title: "6. Third-Party Services",
        content: "Our website may contain links to third-party websites or services that are not operated by us. We have no control over and assume no responsibility for the content, privacy policies, or practices of any third-party sites or services."
      },
      {
        title: "7. Your Rights (GDPR)",
        content: "If you are a European resident, you have the right to access, rectify, or delete your personal data. You also have the right to data portability and the right to restrict or object to the processing of your data."
      },
      {
        title: "8. Data Retention",
        content: "We retain your personal information only for as long as necessary to fulfill the purposes for which it was collected and to comply with legal obligations. Project-related data may be retained for up to 5 years for reference purposes."
      },
      {
        title: "9. Children's Privacy",
        content: "Our services are not directed to individuals under the age of 16. We do not knowingly collect personal information from children. If we become aware that we have collected personal data from a child, we will take steps to delete such information."
      },
      {
        title: "10. Changes to This Policy",
        content: "We may update this privacy policy from time to time. We will notify you of any changes by posting the new policy on this page and updating the 'Last updated' date."
      },
      {
        title: "11. Contact Us",
        content: "If you have any questions about this Privacy Policy or our data practices, please contact us at: contact@azurys.fr"
      }
    ]
  },
  fr: {
    backToHome: "Retour à l'accueil",
    title: "Politique de",
    titleHighlight: "Confidentialité",
    lastUpdated: "Dernière mise à jour : Janvier 2026",
    sections: [
      {
        title: "1. Informations Collectées",
        content: "Nous collectons les informations que vous nous fournissez directement, telles que votre nom, adresse email, numéro de téléphone et toute autre information que vous choisissez de fournir lorsque vous nous contactez ou utilisez nos services."
      },
      {
        title: "2. Utilisation de Vos Informations",
        content: "Nous utilisons les informations collectées pour fournir, maintenir et améliorer nos services, communiquer avec vous sur les projets et mises à jour, vous envoyer des avis techniques et messages d'assistance, et répondre à vos commentaires et questions."
      },
      {
        title: "3. Partage des Informations",
        content: "Nous ne partageons, vendons ni louons vos informations personnelles à des tiers à des fins marketing. Nous pouvons partager des informations avec des prestataires de services qui nous aident à exploiter notre site web et à mener nos activités."
      },
      {
        title: "4. Sécurité des Données",
        content: "Nous mettons en œuvre des mesures techniques et organisationnelles appropriées pour protéger vos informations personnelles contre l'accès, la modification, la divulgation ou la destruction non autorisés. Cependant, aucune méthode de transmission sur Internet n'est sécurisée à 100%."
      },
      {
        title: "5. Cookies et Suivi",
        content: "Nous pouvons utiliser des cookies et des technologies de suivi similaires pour suivre l'activité sur notre site web et conserver certaines informations. Vous pouvez configurer votre navigateur pour refuser tous les cookies ou pour indiquer quand un cookie est envoyé."
      },
      {
        title: "6. Services Tiers",
        content: "Notre site web peut contenir des liens vers des sites web ou services tiers qui ne sont pas exploités par nous. Nous n'avons aucun contrôle et n'assumons aucune responsabilité concernant le contenu, les politiques de confidentialité ou les pratiques de sites ou services tiers."
      },
      {
        title: "7. Vos Droits (RGPD)",
        content: "Si vous êtes résident européen, vous avez le droit d'accéder, de rectifier ou de supprimer vos données personnelles. Vous avez également le droit à la portabilité des données et le droit de restreindre ou de vous opposer au traitement de vos données."
      },
      {
        title: "8. Conservation des Données",
        content: "Nous conservons vos informations personnelles uniquement le temps nécessaire pour atteindre les objectifs pour lesquels elles ont été collectées et pour nous conformer aux obligations légales. Les données liées aux projets peuvent être conservées jusqu'à 5 ans à des fins de référence."
      },
      {
        title: "9. Confidentialité des Enfants",
        content: "Nos services ne s'adressent pas aux personnes de moins de 16 ans. Nous ne collectons pas sciemment d'informations personnelles auprès d'enfants. Si nous apprenons que nous avons collecté des données personnelles d'un enfant, nous prendrons des mesures pour supprimer ces informations."
      },
      {
        title: "10. Modifications de Cette Politique",
        content: "Nous pouvons mettre à jour cette politique de confidentialité de temps en temps. Nous vous informerons de tout changement en publiant la nouvelle politique sur cette page et en mettant à jour la date de 'Dernière mise à jour'."
      },
      {
        title: "11. Nous Contacter",
        content: "Si vous avez des questions concernant cette Politique de Confidentialité ou nos pratiques en matière de données, veuillez nous contacter à : contact@azurys.fr"
      }
    ]
  }
};

const seoContent = {
  fr: {
    title: "Politique de Confidentialité - Azurys | Agence web Paris",
    description: "Politique de confidentialité d'Azurys. Informations sur la collecte, l'utilisation et la protection de vos données personnelles."
  },
  en: {
    title: "Privacy Policy - Azurys | Web Agency Paris",
    description: "Azurys privacy policy. Information about the collection, use and protection of your personal data."
  }
};

function PrivacyContent() {
  const { lang } = useLanguage();
  const t = translations[lang];
  const seo = seoContent[lang];

  return (
    <>
      <Helmet>
        <html lang={lang} />
        <title>{seo.title}</title>
        <meta name="description" content={seo.description} />
        <link rel="canonical" href="https://www.azurys.fr/privacy" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <HeaderNav />
        
        <main className="pt-32 pb-20">
          <Container className="max-w-4xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Link 
                to="/" 
                className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-8"
              >
                <ArrowLeft size={16} />
                {t.backToHome}
              </Link>

              <h1 className="text-4xl sm:text-5xl font-bold mb-4">
                {t.title} <span className="gradient-text">{t.titleHighlight}</span>
              </h1>
              
              <p className="text-muted-foreground mb-8">{t.lastUpdated}</p>

              <div className="prose prose-invert max-w-none space-y-8">
                {t.sections.map((section, index) => (
                  <motion.section
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    className="p-6 rounded-2xl border border-white/[0.08] bg-card/40 backdrop-blur-sm"
                  >
                    <h2 className="text-2xl font-bold text-foreground mb-4">{section.title}</h2>
                    <p className="text-muted-foreground leading-relaxed">{section.content}</p>
                  </motion.section>
                ))}
              </div>
            </motion.div>
          </Container>
        </main>

        <Footer />
      </div>
    </>
  );
}

export default function Privacy() {
  return (
    <LanguageProvider>
      <PrivacyContent />
    </LanguageProvider>
  );
}
